/**
 * Build normalized run counters from API summary / counters payload.
 */
export function isPosSyncNoiseWarning(warning) {
    // Historical push runs logged unpriced entries as warnings; they are expected
    // skips and must not inflate the "reserves" UI.
    return warning?.code === 'CATALOG_ENTRY_NO_PRICE';
}

export function filterPosSyncDisplayWarnings(warnings) {
    if (!Array.isArray(warnings)) {
        return [];
    }
    return warnings.filter((warning) => !isPosSyncNoiseWarning(warning));
}

export function buildPosSyncRunCounters(run) {
    const summary = run?.summary || {};
    const existing = run?.counters || {};
    const displayWarnings = filterPosSyncDisplayWarnings(summary.warnings);
    const ignoredNoPrice =
        existing.products_push_ignored_no_price
        ?? summary.products_push_ignored_no_price
        ?? 0;
    const skippedUnchanged =
        existing.products_push_skipped
        ?? summary.products_push_skipped
        ?? 0;
    const ignoredTotal =
        existing.products_push_ignored
        ?? summary.products_push_ignored
        ?? (ignoredNoPrice + skippedUnchanged);
    return {
        products_created:
            existing.products_created
            ?? summary.products_pushed_created
            ?? summary.products_created
            ?? summary.articles_created
            ?? 0,
        products_updated:
            existing.products_updated
            ?? summary.products_pushed_updated
            ?? summary.products_updated
            ?? summary.articles_updated
            ?? 0,
        products_push_errors:
            existing.products_push_errors
            ?? summary.products_push_errors
            ?? 0,
        products_push_ignored: ignoredTotal,
        products_push_ignored_no_price: ignoredNoPrice,
        products_push_skipped: skippedUnchanged,
        articles_fetched:
            existing.articles_fetched
            ?? summary.articles_fetched
            ?? 0,
        mappings_created:
            existing.mappings_created
            ?? summary.mappings_created
            ?? 0,
        window_article_count:
            existing.window_article_count
            ?? summary.window_article_count
            ?? 0,
        window_pending_count:
            existing.window_pending_count
            ?? summary.window_pending_count
            ?? 0,
        closures_imported: existing.closures_imported ?? summary.closures_imported ?? 0,
        order_items_created:
            existing.order_items_created ?? summary.order_items_created ?? 0,
        lines_ignored: existing.lines_ignored ?? summary.lines_ignored ?? 0,
        stock_movements_created:
            existing.stock_movements_created ?? summary.stock_movements_created ?? 0,
        warnings_count: displayWarnings.length,
    };
}

export function posSyncCountersSummaryLabel(counters, translate, actionType = null) {
    if (actionType === 'push_catalog') {
        return [
            `${translate('posSync.run.productsCreatedShort')}: ${counters.products_created || 0}`,
            `${translate('posSync.run.productsUpdatedShort')}: ${counters.products_updated || 0}`,
            `${translate('posSync.run.ignoredShort')}: ${counters.products_push_ignored || 0}`,
            `${translate('posSync.run.errorsShort')}: ${counters.products_push_errors || 0}`,
        ].join(' · ');
    }
    if (actionType === 'fetch_articles') {
        return [
            `${translate('posSync.run.articlesFetchedShort')}: ${counters.articles_fetched || 0}`,
            `${translate('posSync.run.productsCreatedShort')}: ${counters.products_created || 0}`,
            `${translate('posSync.run.productsUpdatedShort')}: ${counters.products_updated || 0}`,
        ].join(' · ');
    }
    if (actionType === 'prepare_sales_import') {
        return [
            `${translate('posSync.run.windowArticlesShort')}: ${counters.window_article_count || 0}`,
            `${translate('posSync.run.productsCreatedShort')}: ${counters.mappings_created || 0}`,
            `${translate('posSync.run.windowPendingShort')}: ${counters.window_pending_count || 0}`,
        ].join(' · ');
    }
    const products = (counters.products_created || 0) + (counters.products_updated || 0);
    const parts = [
        `${translate('posSync.run.productsShort')}: ${products}`,
        `${translate('posSync.run.closuresShort')}: ${counters.closures_imported || 0}`,
    ];
    if (counters.order_items_created) {
        parts.push(
            `${translate('posSync.run.orderLinesShort')}: ${counters.order_items_created}`
        );
    }
    if (counters.stock_movements_created) {
        parts.push(
            `${translate('posSync.run.stockMovesShort')}: ${counters.stock_movements_created}`
        );
    }
    return parts.join(' · ');
}
